/**
 * Hero Section Controller
 *
 * Handles:
 * 1. Initial fade-in animation for hero image
 * 2. Initial fade-in animation for hero logo (after image)
 * 3. Slow zoom effect on hero image
 * 4. Scroll-based fade-out for hero logo
 * 5. Site logo visibility toggle on homepage
 *
 * @package EVT Restaurant Builder
 * @since 1.0.0
 */

class HeroLogoController {
    constructor() {
        this.heroSection = null;
        this.heroImage = null;
        this.siteLogo = null;
        this.heroBrandLogo = null;
        this.isHomepage = false;
        this.isFirstChild = false;
        this.heroHeight = 0;
        this.threshold = 0.75;
        this.isLogoVisible = true;
        this.desktopBreakpoint = 1024;

        // Animation state
        this.animationComplete = false;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Check if viewport is desktop size
     */
    isDesktop() {
        return window.innerWidth >= this.desktopBreakpoint;
    }

    /**
     * Setup the hero controller
     */
    setup() {
        if (!this.findHeroSection()) {
            console.log('HeroLogoController: Hero section not found.');
            return;
        }

        // Check if we're on homepage and if hero is first child
        this.checkPageContext();

        // Hide site logo on homepage only if hero is first child
        if (this.isHomepage && this.isFirstChild && this.siteLogo) {
            this.setLogoOpacity(0);
            this.isLogoVisible = false;
        }

        // Find elements
        this.heroBrandLogo = this.heroSection.querySelector('.hero-section_brand-logo');
        this.heroImage = this.heroSection.querySelector('.wp-block-cover__image-background');

        // Calculate hero section height
        this.calculateHeroHeight();

        // Run entry animations
        this.runEntryAnimations();

        // Only bind scroll events if hero is first child
        if (this.isFirstChild) {
            this.bindScrollEvent();
            this.bindResizeEvent();
        }
    }

    /**
     * Run the initial entry animations
     */
    runEntryAnimations() {
        // Skip animations if user prefers reduced motion
        if (this.prefersReducedMotion) {
            if (this.heroImage) this.heroImage.style.opacity = '1';
            if (this.heroBrandLogo) this.heroBrandLogo.style.opacity = '1';
            this.animationComplete = true;
            return;
        }

        const imageDelay = 100; // Short delay before image starts fading in

        // Phase 1: Fade in the background image (after short delay)
        if (this.heroImage) {
            this.heroImage.style.transform = 'scale(1)';
            this.heroImage.style.transition = 'opacity 1.2s ease-out';

            setTimeout(() => {
                this.heroImage.style.opacity = '1';

                // Start slow zoom after image fades in
                setTimeout(() => {
                    this.heroImage.style.transition = 'transform 20s ease-out';
                    this.heroImage.style.transform = 'scale(1.08)';
                }, 800);
            }, imageDelay);
        }

        // Phase 2: Fade in the logo (after image starts + 1s, 1s duration)
        const logoDelay = imageDelay + 600;

        if (this.heroBrandLogo) {
            this.heroBrandLogo.style.transition = 'opacity 1s ease-out';

            setTimeout(() => {
                this.heroBrandLogo.style.opacity = '1';

                // Mark animation complete after logo fade finishes
                setTimeout(() => {
                    this.animationComplete = true;
                    // Remove transition so scroll-based opacity works immediately
                    this.heroBrandLogo.style.transition = '';
                }, 800);
            }, logoDelay);
        } else {
            // No logo, mark complete after image fades in
            setTimeout(() => {
                this.animationComplete = true;
            }, imageDelay + 1200);
        }
    }

    /**
     * Find hero section
     */
    findHeroSection() {
        const entryContent = document.querySelector('.entry-content');
        if (!entryContent) {
            return false;
        }

        const firstChild = entryContent.firstElementChild;
        if (!firstChild || !firstChild.classList.contains('hero-full')) { 
            return false;
        }

        this.heroSection = firstChild;
        this.isFirstChild = true;
        
        return true;
    }

    /**
     * Check page context and find site logo
     */
    checkPageContext() {
        const body = document.body;
        this.isHomepage = body.classList.contains('home') ||
                         body.classList.contains('front-page');

        this.siteLogo = document.querySelector('.site-header .site-logo');
    }

    /**
     * Calculate hero section height
     */
    calculateHeroHeight() {
        if (this.heroSection) {
            this.heroHeight = this.heroSection.offsetHeight;
        }
    }

    /**
     * Bind scroll event with throttling
     */
    bindScrollEvent() {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
    }

    /**
     * Bind resize event
     */
    bindResizeEvent() {
        let resizeTimeout;

        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.calculateHeroHeight();
                this.handleScroll();
            }, 150);
        };

        window.addEventListener('resize', handleResize);
    }

    /**
     * Handle scroll event
     */
    handleScroll() {
        if (!this.heroSection || !this.isFirstChild) return;

        const scrollY = window.scrollY || window.pageYOffset;

        // Handle hero brand logo fade (only after animation completes)
        this.handleHeroBrandLogoFade(scrollY);

        // Site logo show/hide only applies to homepage
        const heroRect = this.heroSection.getBoundingClientRect();
        const heroVisibleHeight = Math.max(0, heroRect.bottom);
        const heroThreshold = this.heroHeight * this.threshold;

        const isHeroVisible = heroVisibleHeight > heroThreshold;

        if (isHeroVisible && this.isLogoVisible) {
            this.setLogoOpacity(0);
            this.isLogoVisible = false;
        } else if (!isHeroVisible && !this.isLogoVisible) {
            this.setLogoOpacity(1);
            this.isLogoVisible = true;
        }
    }

    /**
     * Handle hero brand logo fade based on scroll position
     */
    handleHeroBrandLogoFade(scrollY) {
        if (!this.heroBrandLogo) return;

        // Don't interfere with entry animation
        if (!this.animationComplete) return;

        const fadeDistance = this.heroHeight * 0.75;
        const opacity = Math.max(0, 1 - (scrollY / fadeDistance));

        this.heroBrandLogo.style.opacity = opacity;
    }

    /**
     * Set site logo opacity with smooth transition
     */
    setLogoOpacity(opacity) {
        if (!this.siteLogo) return;

        this.siteLogo.style.transition = 'opacity 0.3s ease-in-out';
        this.siteLogo.style.opacity = opacity;
    }
}

// Initialize hero controller
export function initHeroLogoController() {
    const controller = new HeroLogoController();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => controller.setup());
    } else {
        controller.setup();
    }

    return controller;
}
